// src/web/routes/login.tsx

import { createFileRoute } from '@tanstack/react-router'
import { useEffect } from 'react'

// Main domain login URL - users authenticate there and get SSO cookie
// In development, use local frnd.test; in production, use frndos.com
const isDev = import.meta.env.DEV
const MAIN_LOGIN_URL =
	import.meta.env.VITE_SSO_LOGIN_URL ||
	(isDev ? 'https://frnd.test:3000/login' : 'https://frndos.com/login')

export const Route = createFileRoute('/login')({
	component: LoginPage,
})

function LoginPage() {
	// Check if user already has SSO session via API (cookie is HttpOnly)
	useEffect(() => {
		fetch('/api/auth/check', { credentials: 'include' })
			.then((res) => {
				if (res.ok) {
					// Already has session, redirect to app
					window.location.href = '/home'
				}
			})
			.catch(() => {
				// Ignore errors, stay on login page
			})
	}, [])

	const handleLogin = () => {
		const redirectUri = encodeURIComponent(`${window.location.origin}/home`)
		window.location.href = `${MAIN_LOGIN_URL}?type=sso&provider=dam&redirect_uri=${redirectUri}`
	}

	return (
		<div className="min-h-screen flex bg-background">
			{/* Left side - decorative */}
			<div className="hidden lg:flex lg:w-1/2 xl:w-[55%] bg-neutral-950 relative overflow-hidden">
				{/* Gradient orbs */}
				<div className="absolute inset-0">
					<div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-[120px]" />
					<div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-primary/15 rounded-full blur-[100px]" />
					<div className="absolute top-1/2 right-1/3 w-64 h-64 bg-primary/10 rounded-full blur-[80px]" />
				</div>

				{/* Grid pattern */}
				<div
					className="absolute inset-0 opacity-[0.03]"
					style={{
						backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
						backgroundSize: '64px 64px',
					}}
				/>

				{/* Content */}
				<div className="relative z-10 flex flex-col justify-between p-12 xl:p-16">
					{/* Logo */}
					<div className="flex items-center gap-3">
						<div className="w-10 h-10 rounded-xl bg-secondary flex items-center justify-center overflow-hidden">
							<img src="/favicon.svg" alt="DAM Logo" className="w-6 h-6" />
						</div>
						<span className="text-secondary font-semibold text-lg tracking-tight">DAM</span>
					</div>

					{/* Main text */}
					<div className="max-w-md">
						<h1 className="text-4xl xl:text-5xl font-semibold text-secondary leading-tight tracking-tight">
							Manage your digital assets with precision
						</h1>
						<p className="mt-6 text-lg text-neutral-400 leading-relaxed">
							A powerful platform for organizing, searching, and sharing your media files
							efficiently.
						</p>
					</div>

					{/* Footer */}
					<div className="text-sm text-neutral-500">
						<p>Secure. Fast. Beautiful.</p>
					</div>
				</div>
			</div>

			{/* Right side - login prompt */}
			<div className="flex-1 flex flex-col justify-center px-6 py-12 lg:px-12 xl:px-20 bg-background">
				<div className="w-full max-w-sm mx-auto">
					{/* Mobile logo */}
					<div className="lg:hidden flex items-center justify-center gap-2.5 mb-12">
						<div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center overflow-hidden">
							<img src="/favicon.svg" alt="DAM Logo" className="w-6 h-6" />
						</div>
						<span className="font-semibold text-lg text-foreground tracking-tight">DAM</span>
					</div>

					{/* Header */}
					<div className="mb-8 text-center lg:text-left">
						<h2 className="text-2xl font-semibold text-foreground tracking-tight">
							Welcome to DAM
						</h2>
						<p className="mt-2 text-[15px] text-muted-foreground">
							Sign in with your frndos.com account to continue
						</p>
					</div>

					{/* SSO Login Button */}
					<button
						type="button"
						onClick={handleLogin}
						className="w-full py-3 px-4 rounded-lg font-medium text-[15px] bg-primary text-primary-foreground hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2 dark:focus:ring-offset-background transition-all duration-150 shadow-sm hover:shadow-md flex items-center justify-center gap-2"
					>
						<svg
							className="w-5 h-5"
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
							aria-hidden="true"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
							/>
						</svg>
						Continue with frndos.com
					</button>

					{/* Info text */}
					<p className="mt-6 text-center text-sm text-muted-foreground">
						You'll be redirected to frndos.com to sign in securely.
					</p>
				</div>
			</div>
		</div>
	)
}
